import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Redirect, RouteComponentProps, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader } from '@carsys/ui'
import { IExternalGMS } from '@cbgms/api'
import { useNotifier } from '@cbgms/base/notifier'
import { useQuery } from '@cbgms/base/http/use-query'
import ExternalGMSForm from 'gms-support/src/app-support/external-gms/components/ExternalGMSForm'
import { deleteExternalGMS, getExternalGMS, updateExternalGMS } from 'gms-support/src/app-support/external-gms/state/actions'
import { IDeleteOptions } from 'core/page/layout/FormikFormLayout'

type Props = RouteComponentProps<{ externalGMSUUID: string }>

const EditExternalGMS = (props: Props) => {
  const {
    match: {
      params: { externalGMSUUID }
    }
  } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const history = useHistory()
  const initialExternalGMS = useQuery(['EditExternalGMS', 'getExternalGMS', externalGMSUUID], async () => {
    const { Data } = await dispatch(getExternalGMS(externalGMSUUID))
    return Data
  })

  const onSubmit = async (values: IExternalGMS) => {
    try {
      await dispatch(updateExternalGMS(externalGMSUUID, values))
      return values
    } catch (error) {
      throw error
    }
  }

  const onSubmitSuccess = async () => {
    notifier.success(t('external_gms.successfully_edited'))
  }

  const deleteOptions = useMemo(
    (): IDeleteOptions => ({
      onDelete: async dryRun => {
        await dispatch(deleteExternalGMS(externalGMSUUID, dryRun))
        return { Data: { Rejections: [], Warnings: [] } }
      },
      onDeleteSuccess: async () => history.push('//external-gms'),
      itemName: initialExternalGMS.data?.Name || '',
      deleteScope: 'external_gms'
    }),
    [externalGMSUUID, initialExternalGMS.data?.Name]
  )

  if (initialExternalGMS.isLoading || !initialExternalGMS.data) {
    return <Loader loading />
  }

  if (initialExternalGMS.isError) {
    notifier.error(t('global.something_went_wrong'))

    return <Redirect to='/external-gms' />
  }

  return (
    <ExternalGMSForm
      headerText='external_gms.title'
      onSubmit={onSubmit}
      onSubmitSuccess={onSubmitSuccess}
      deleteOptions={deleteOptions}
      form='edit_external_gms'
      initialValues={initialExternalGMS.data}
      enableReinitialize
    />
  )
}

export default EditExternalGMS
